import { changePushBinding, recoverPushOperation, hasPushOperation, logoutPushBinding, pushAccount } from './pushOwnership';
// Hosted iOS registration. No provider send is performed here.
const AUTH_HEADER_KEY = 'access_token';
const LAST_REGISTERED_TOKEN_KEY = 'push_last_registered_token';
// Pre-protocol markers are only cleared after acknowledged v2 setup. Release
// requires retirement of every old backend writer before serving this client.
const PENDING_KEY = 'push_registration_unresolved'; // pre-protocol local marker; never silently expire
const getAccessToken = () => localStorage.getItem(AUTH_HEADER_KEY);
const getApiBase = () => `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const getPushPlugin = () => {
  const cap = window.Capacitor;
  try {
    const platform = cap?.getPlatform?.() ?? cap?.platform;
    if (platform !== 'ios' || (cap?.isNativePlatform && !cap.isNativePlatform())) return null;
  }
  catch { return null; }
  return cap?.Plugins?.PushNotifications || null;
};
const tokenTail = (t) => {
  if (!t || typeof t !== 'string') return null;
  return t.length >= 12 ? t.slice(-6) : null;
};

function reportPushDiagnostic(event, details) {
  try {
    const body = {
      error_message: `push:${event}`,
      stack_trace: details ? JSON.stringify(details).slice(0, 3500) : '',
      bundle: 'push-diag',
      url: (typeof window !== 'undefined' && window.location && window.location.pathname) || '',
      timestamp: new Date().toISOString(),
    };
    // Fire-and-forget. Do NOT await. Catch all.
    fetch(`${getApiBase()}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-OpenRuns-Client-Protocol': '2' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // noop — telemetry must never break the host flow.
  }
}


let generation = 0;
let running = null;
let silentRecovery = null;
export function abandonPushRegistration() {
  generation += 1;
  silentRecovery?.cancelWait?.();
}
const outcome = (code, retryable = false) => ({ code, retryable });
const bounded = (promise, ms = 10000) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('timeout')), ms);
  Promise.resolve(promise).then(resolve, reject).finally(() => clearTimeout(timer));
});
const nativeFailure = (error) => {
  const message = String(error?.error || error?.message || error || '');
  return /aps-environment|entitlement|provisioning|signing|not configured/i.test(message)
    ? outcome('configuration_required') : outcome('native_failed', true);
};
export async function getPushPermissionState() {
  const plugin = getPushPlugin();
  if (!plugin) return 'unavailable';
  try {
    const state = (await bounded(plugin.checkPermissions()))?.receive;
    return ['granted', 'denied', 'prompt'].includes(state) ? state : 'unknown';
  } catch { return 'unknown'; }
}
export async function requestPushPermission() {
  const plugin = getPushPlugin();
  if (!plugin) return 'unavailable';
  try { return (await bounded(plugin.requestPermissions(), 30000))?.receive || 'unavailable'; }
  catch { return 'unavailable'; }
}

/** Explicit action: permission followed by acknowledged registration. Automatic
 * launch/login calls use requestPermission=false and never prompt. */
export function enablePushNotifications({ requestPermission = true, isCurrent = () => true } = {}) {
  if (running) return Promise.resolve(outcome('busy', true));
  const access = getAccessToken();
  const started = generation;
  const current = () => started === generation && access === getAccessToken() && isCurrent();
  if (!access) return Promise.resolve(outcome('sign_in_required'));
  const task = async () => {
    const plugin = getPushPlugin();
    if (!plugin) return outcome('unavailable');
    try {
      if (hasPushOperation(access)) {
        const recovered = await recoverPushOperation(access, current);
        if (!current()) return outcome('abandoned');
        if (recovered.code !== 'registration_needed') return recovered;
      }
    }
    catch { return outcome('storage_unavailable'); }
    let permission = await getPushPermissionState();
    reportPushDiagnostic('permission_state', {state: permission, where: requestPermission ? 'settings_or_sheet' : 'registerIfAlreadyGranted'});
    if (!current()) return outcome('abandoned');
    if (permission === 'prompt' && requestPermission) permission = await requestPushPermission();
    if (!current()) return outcome('abandoned');
    if (permission !== 'granted') return outcome(permission);

    const handles = [];
    let listening = true;
    let settle;
    const event = new Promise(resolve => { settle = resolve; });
    let received = false;
    const finish = value => {
      if (listening && !received && current()) { received = true; settle(value); }
    };
    const listen = async (name, handler) => {
      // Late handles are removed even if setup timed out or the account left.
      const pending = Promise.resolve(plugin.addListener(name, handler)).then(handle => {
        if (!listening || !current()) { Promise.resolve(handle?.remove?.()).catch(() => {}); return; }
        handles.push(handle);
      });
      await bounded(pending);
    };
    try {
      await listen('registration', data => finish({ token: typeof data === 'string' ? data : data?.value || data?.token }));
      await listen('registrationError', error => finish({ failure: nativeFailure(error) }));
      if (!current()) return outcome('abandoned');
      reportPushDiagnostic('register_invoking', {});
      await bounded(plugin.register());
      reportPushDiagnostic('register_returned', {});
      const result = await bounded(event);
      if (!current()) return outcome('abandoned');
      if (result.failure) { reportPushDiagnostic('registrationError', {error:result.failure.code}); return result.failure; }
      if (typeof result.token !== 'string' || result.token.length < 16) return outcome('invalid_token', true);
      reportPushDiagnostic('registration_event', {token_tail:tokenTail(result.token), environment:'production'});
      if (!window.Capacitor?.Plugins?.Device?.getId ||
          (typeof window.Capacitor.isPluginAvailable === 'function' && !window.Capacitor.isPluginAvailable('Device'))) {
        return outcome('app_update_required');
      }
      let deviceId, appVersion;
      try {
        const device = window.Capacitor?.Plugins?.Device;
        const id = device?.getId ? await bounded(device.getId(), 3000) : null;
        deviceId = id?.identifier || id?.uuid;
        if (device?.getInfo) appVersion = (await bounded(device.getInfo(), 3000))?.appVersion;
      } catch (error) {
        if (error?.code === 'UNIMPLEMENTED') return outcome('app_update_required');
        // Other bridge/metadata failures remain retryable, not proof of an old binary.
      }
      if (!current()) return outcome('abandoned');
      if (!deviceId) return outcome('device_unavailable', true);
      const registration = await changePushBinding({ token: result.token, platform: 'ios', environment: 'production',
        device_id: deviceId, app_version: appVersion, locale: navigator?.language }, access, current);
      if (registration.code === 'registered' && current()) {
        localStorage.setItem(LAST_REGISTERED_TOKEN_KEY, result.token);
        localStorage.removeItem(PENDING_KEY);
      }
      return registration;

    } catch (error) { return current() ? nativeFailure(error) : outcome('abandoned'); }
    finally {
      listening = false;
      for (const handle of handles) Promise.resolve(handle?.remove?.()).catch(() => {});
    }
  };
  running = task().finally(() => { running = null; });
  return running;
}
export async function registerForPush() {
  return (await enablePushNotifications({ requestPermission: false })).code === 'registered';
}
export async function registerIfAlreadyGranted() { return registerForPush(); }

// Lifecycle recovery reuses the same registration/receipt coordinator. A token
// refresh invalidates the old attempt; the next bounded attempt uses fresh auth.
export function recoverPushRegistrationSilently() {
  const access = getAccessToken();
  const account = pushAccount(access);
  if (!access || !account) return Promise.resolve(outcome('sign_in_required'));
  if (!getPushPlugin()) return Promise.resolve(outcome('unavailable'));
  if (silentRecovery?.generation === generation && silentRecovery.account === account) {
    return silentRecovery.promise;
  }
  const job = { generation, account, cancelWait: null };
  const current = () => generation === job.generation && pushAccount(getAccessToken()) === account;
  const wait = ms => new Promise(resolve => {
    const finish = () => { clearTimeout(timer); job.cancelWait = null; resolve(current()); };
    const timer = setTimeout(finish, ms);
    job.cancelWait = finish;
  });
  job.promise = (async () => {
    // Wait for an explicit setup or a departed account's bounded native attempt.
    if (running) await running.catch(() => {});
    let last = outcome('abandoned');
    for (let attempt = 0; attempt < 3 && current(); attempt += 1) {
      const attemptAccess = getAccessToken();
      try {
        // Ownership requests intentionally expose generic failures. This read
        // distinguishes confirmed auth denial without changing ownership rules.
        const auth = await bounded(fetch(`${getApiBase()}/auth/me`, {
          headers: { Authorization: `Bearer ${attemptAccess}`, 'X-OpenRuns-Client-Protocol': '2' },
        }));
        if (!current()) return outcome('abandoned');
        if (attemptAccess !== getAccessToken()) continue;
        if (auth.status === 401 || auth.status === 403) return outcome('unauthorized');
        if (!auth.ok) last = outcome('context_failed', true);
        else last = await enablePushNotifications({ requestPermission: false, isCurrent: current });
      } catch { last = outcome('context_failed', true); }
      if (!current()) return outcome('abandoned');
      if (attemptAccess !== getAccessToken()) continue;
      if (!['native_failed', 'device_unavailable', 'context_failed', 'upload_uncertain', 'status_unknown', 'busy', 'unknown'].includes(last.code)) return last;
      if (attempt < 2 && !await wait(attempt === 0 ? 1000 : 4000)) return outcome('abandoned');
    }
    return current() ? last : outcome('abandoned');
  })().finally(() => { if (silentRecovery === job) silentRecovery = null; });
  silentRecovery = job;
  return job.promise;
}

export function bindPushRecoveryLifecycle() {
  const recover = () => { recoverPushRegistrationSilently().catch(() => {}); };
  const visible = () => { if (document.visibilityState === 'visible') recover(); };
  document.addEventListener('visibilitychange', visible);
  window.addEventListener('focus', visible);
  window.addEventListener('online', recover);
  return () => {
    document.removeEventListener('visibilitychange', visible);
    window.removeEventListener('focus', visible);
    window.removeEventListener('online', recover);
  };
}

const ALLOWED_DEEP_LINK_PREFIXES = ['/r/', '/run/', '/club/', '/games/', '/notifications'];

const isAllowedDeepLink = (path) => {
  if (!path || typeof path !== 'string' || !path.startsWith('/')) return false;
  // Awareness pushes target this exact authenticated route, not its prefixes.
  if (path === '/dashboard') return true;
  return ALLOWED_DEEP_LINK_PREFIXES.some((p) => path.startsWith(p));
};

/**
 * Wire the tap handler so push taps route to the right SPA screen. Call
 * once at app mount with the React Router navigate fn.
 */
export function bindNotificationTapHandler(navigate) {
  const plugin = getPushPlugin();
  if (!plugin || typeof navigate !== 'function') return () => {};

  let pendingHandle;
  const onAction = (event) => {
    try {
      const notif = event?.notification || event || {};
      const data = notif?.data || {};
      const deepLink = data?.deep_link || data?.deepLink;
      if (isAllowedDeepLink(deepLink)) {
        navigate(deepLink, { replace: false });
      } else {
        navigate('/notifications', { replace: false });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Push] Tap handler failed:', e?.message);
    }
  };

  try {
    pendingHandle = plugin.addListener('pushNotificationActionPerformed', onAction);
  } catch { /* noop */ }

  return () => {
    Promise.resolve(pendingHandle)
      .then((h) => h?.remove?.())
      .catch(() => {});
  };
}

// ---------------------------------------------------------------------------
// Logout does not wait indefinitely for native listeners or network work.
// An unresolved upload fences subsequent registrations, including after reload.
export async function unregisterOnLogout({ access = getAccessToken(), isCurrent = null } = {}) {
  if (isCurrent && !isCurrent()) return false;
  abandonPushRegistration();
  if (!getPushPlugin()) return true;
  const started = generation;
  const current = () => generation === started && (isCurrent ? isCurrent() : access === getAccessToken());
  if (!access) return false;
  const cleanup = async () => {
    let legacyDevice = null;
    const token = localStorage.getItem(LAST_REGISTERED_TOKEN_KEY);
    if (token) {
      const plugin = window.Capacitor?.Plugins?.Device;
      const identity = plugin?.getId ? await bounded(plugin.getId(), 3000) : null;
      const deviceId = identity?.identifier || identity?.uuid;
      if (!deviceId) return false; // A cached token is not evidence of remote cleanup.
      if (deviceId) legacyDevice = { token, device_id: deviceId, platform: 'ios', environment: 'production' };
    }
    if (!current()) return false;
    return logoutPushBinding(access, current, legacyDevice);
  };
  try { return await bounded(cleanup(), 5000); }
  catch { return false; }
}
